import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Trophy, TrendingUp, Calendar, Download, Settings2, Sparkles, Medal, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KPI_FIELDS = [
  { key: 'loans_captured',        label: 'Loans Captured',      defaultWeight: 2 },
  { key: 'drivers_onboarded',     label: 'Drivers Onboarded',   defaultWeight: 10 },
  { key: 'statements_uploaded',   label: 'Statements Uploaded', defaultWeight: 3 },
  { key: 'parts_images_captured', label: 'Parts Images',        defaultWeight: 1 },
  { key: 'stock_loaded',          label: 'Stock Loads',         defaultWeight: 5 },
  { key: 'tasks_completed',       label: 'Tasks Completed',     defaultWeight: 4 },
];

const WEIGHTS_KEY = 'jsm_kpi_weights_v1';

const loadWeights = () => {
  try {
    const raw = localStorage.getItem(WEIGHTS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return Object.fromEntries(KPI_FIELDS.map(f => [f.key, f.defaultWeight]));
};

const saveWeights = (w) => {
  try { localStorage.setItem(WEIGHTS_KEY, JSON.stringify(w)); } catch {}
};

const PRESETS = [
  { label: 'Today',      fn: () => { const d = new Date().toISOString().slice(0,10); return { start: d, end: d }; } },
  { label: 'Yesterday',  fn: () => { const d = new Date(); d.setDate(d.getDate()-1); const s = d.toISOString().slice(0,10); return { start: s, end: s }; } },
  { label: 'Last 7 days',fn: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate()-6); return { start: s.toISOString().slice(0,10), end: e.toISOString().slice(0,10) }; } },
  { label: 'This month', fn: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth(), 1).toISOString().slice(0,10); return { start: s, end: n.toISOString().slice(0,10) }; } },
  { label: 'Last month', fn: () => {
      const n = new Date();
      const firstThis = new Date(n.getFullYear(), n.getMonth(), 1);
      const lastPrev = new Date(firstThis); lastPrev.setDate(0);
      const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1);
      return { start: firstPrev.toISOString().slice(0,10), end: lastPrev.toISOString().slice(0,10) };
    } },
];

export default function ClerkPerformance() {
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weights, setWeights] = useState(loadWeights);
  const [showWeights, setShowWeights] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/clerk-workbench/team-kpis`, {
        headers,
        params: { start_date: startDate, end_date: endDate },
      });
      setData(res.data);
    } catch {
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setPreset = (preset) => {
    const r = preset.fn();
    setStartDate(r.start); setEndDate(r.end);
  };

  const updateWeight = (key, value) => {
    const next = { ...weights, [key]: Math.max(0, parseFloat(value) || 0) };
    setWeights(next); saveWeights(next);
  };

  // Compute scores + rankings
  const ranked = useMemo(() => {
    if (!data?.clerks) return [];
    const withScores = data.clerks.map(c => {
      let score = 0;
      for (const f of KPI_FIELDS) score += (c[f.key] || 0) * (weights[f.key] || 0);
      const taskRate = c.tasks_assigned > 0 ? (c.tasks_completed / c.tasks_assigned) * 100 : null;
      return { ...c, score, task_rate: taskRate };
    });
    // Sort by score desc
    withScores.sort((a, b) => b.score - a.score);
    return withScores;
  }, [data, weights]);

  const topScore = ranked[0]?.score || 0;
  const totalClerks = ranked.length;
  const teamTotalScore = ranked.reduce((a, b) => a + b.score, 0);
  const avgScore = totalClerks > 0 ? teamTotalScore / totalClerks : 0;

  const rankIcon = (i) => {
    if (i === 0) return <Trophy className="h-5 w-5 text-yellow-500" />;
    if (i === 1) return <Medal className="h-5 w-5 text-slate-400" />;
    if (i === 2) return <Award className="h-5 w-5 text-amber-700" />;
    return <span className="text-slate-500 text-xs font-mono">#{i + 1}</span>;
  };

  const exportCsv = () => {
    if (!ranked.length) { toast.error('Nothing to export'); return; }
    const headerRow = ['Rank', 'Clerk', 'Score', ...KPI_FIELDS.map(f => f.label), 'Tasks Completed / Assigned', 'Task Rate %'];
    const rows = ranked.map((c, i) => [
      i + 1, c.full_name || c.username, c.score,
      ...KPI_FIELDS.map(f => c[f.key] || 0),
      `${c.tasks_completed}/${c.tasks_assigned}`,
      c.task_rate != null ? c.task_rate.toFixed(0) : '-',
    ]);
    const csv = [headerRow, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `clerk_performance_${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Performance report downloaded');
  };

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Trophy className="h-7 w-7 text-yellow-500" />
              Clerk Performance &amp; Incentives
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Leaderboard and configurable points for bonus / incentive calculation. {data?.period && <span className="font-semibold">— {data.period}</span>}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowWeights(!showWeights)} data-testid="toggle-weights-btn">
              <Settings2 className="h-4 w-4 mr-1.5" /> {showWeights ? 'Hide' : 'Configure'} Points
            </Button>
            <Button variant="outline" onClick={exportCsv} data-testid="export-perf-btn">
              <Download className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Date range */}
        <Card>
          <CardContent className="p-3 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-44 h-8" data-testid="perf-start-date" />
            </div>
            <div>
              <Label className="text-xs">End date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-44 h-8" data-testid="perf-end-date" />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {PRESETS.map(p => (
                <Button key={p.label} size="sm" variant="outline" onClick={() => setPreset(p)} className="h-8" data-testid={`preset-${p.label.replace(/\s+/g, '-')}`}>
                  <Calendar className="h-3 w-3 mr-1" /> {p.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Weights editor */}
        {showWeights && (
          <Card className="border-amber-500/40 bg-amber-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" /> Incentive Points per Action
              </CardTitle>
              <p className="text-xs text-muted-foreground">Define how many points each activity is worth. Used to calculate the leaderboard score below. Saved locally.</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {KPI_FIELDS.map(f => (
                  <div key={f.key}>
                    <Label className="text-xs">{f.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={weights[f.key] ?? 0}
                      onChange={(e) => updateWeight(f.key, e.target.value)}
                      className="h-8 text-center font-bold"
                      data-testid={`weight-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground">Clerks Tracked</div>
            <div className="text-2xl font-bold">{totalClerks}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground">Team Total Score</div>
            <div className="text-2xl font-bold text-indigo-500">{teamTotalScore.toLocaleString()}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground">Average Score</div>
            <div className="text-2xl font-bold">{avgScore.toFixed(1)}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground">Top Scorer</div>
            <div className="text-lg font-bold truncate">{ranked[0]?.full_name || ranked[0]?.username || '—'}</div>
            <div className="text-xs text-yellow-600 font-semibold">{topScore} pts</div>
          </CardContent></Card>
        </div>

        {/* Leaderboard */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-500" /> Performance Leaderboard
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : ranked.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">No clerks found for this period.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14 text-center">Rank</TableHead>
                    <TableHead>Clerk</TableHead>
                    {KPI_FIELDS.map(f => <TableHead key={f.key} className="text-center">{f.label}</TableHead>)}
                    <TableHead className="text-center">Tasks</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead className="text-right w-40">Relative</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ranked.map((c, i) => {
                    const pct = topScore > 0 ? (c.score / topScore) * 100 : 0;
                    return (
                      <TableRow key={c.id} data-testid={`perf-row-${c.id}`}>
                        <TableCell className="text-center">{rankIcon(i)}</TableCell>
                        <TableCell className="font-semibold">{c.full_name || c.username}</TableCell>
                        {KPI_FIELDS.map(f => (
                          <TableCell key={f.key} className="text-center">
                            {c[f.key] || 0}
                            {(c[f.key] || 0) > 0 && (
                              <div className="text-[10px] text-muted-foreground">
                                {((c[f.key] || 0) * (weights[f.key] || 0)).toFixed(0)} pts
                              </div>
                            )}
                          </TableCell>
                        ))}
                        <TableCell className="text-center text-xs">
                          {c.tasks_completed}/{c.tasks_assigned}
                          {c.task_rate != null && (
                            <div className="text-[10px] text-muted-foreground">{c.task_rate.toFixed(0)}%</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-extrabold text-indigo-600 text-lg">{c.score}</TableCell>
                        <TableCell>
                          <div className="w-full bg-muted rounded h-2 overflow-hidden">
                            <div
                              className={`h-full ${i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-amber-700' : 'bg-indigo-500'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="text-[10px] text-right text-muted-foreground mt-0.5">{pct.toFixed(0)}%</div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
